import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useAppContext } from '../../app/Context';
import { classFactory } from '../../db/classes/helpers';
import { courseFactory } from '../../db/courses/helpers';
import { createPoll as createPollAPI, createPollQuestion, setSlideType } from '../../db/polls';
import { pollFactory } from '../../db/polls/helpers';
import registerInterceptors from '../../db/request-interceptors';
import { PollSlideTypes, ToBeDone } from '../../db/types';
import { i18nNS } from '../../i18n';
import { useClassContext } from '../../modules/class/Context';
import { useCourseContext } from '../../modules/course/Context';
import { usePollContext } from '../../modules/poll/Context';
import routes, { NewPollParams } from '../../routes/router';
import { enumFromStringValue } from '../../utils/string';
import { setActivityType } from '../../db/shared';
import { SlideType } from '../../db/shared/types';

const useNewPollPage = () => {
  const {
    universitySlug,
    courseId,
    classId,
    toBeDone: toBeDoneValues,
    slideType: slideTypeValues,
  } = useParams<NewPollParams>();
  const { t } = useTranslation([i18nNS.POLL, i18nNS.COMMON]);
  const toBeDone = enumFromStringValue(ToBeDone, toBeDoneValues);
  const slideType = enumFromStringValue(PollSlideTypes, slideTypeValues);

  const { presentationId, activeSlideId, session, setError, setSession, setSlideTitleAndSubtitle } =
    useAppContext();

  useEffect(() => {
    if (!courseId) return;
    registerInterceptors({
      token: session?.token ?? null,
      cluster: session?.cluster ?? null,
      universitySlug: session?.university.slug ?? null,
      courseId,
      setError,
      setSession,
    });
  }, [session?.token, session?.cluster, session?.university.slug, courseId, setError]);

  const { setCourse } = useCourseContext();
  const { setClass } = useClassContext();
  const { setPoll } = usePollContext();

  const navigate = useNavigate();

  const createPoll = useCallback(async () => {
    if (!session?.userId) return;
    if (!universitySlug) return;
    if (!courseId) return;
    if (!classId) return;
    if (!toBeDone) return;
    if (!slideType) return;
    if (!presentationId) return;
    if (!activeSlideId) return;

    const {
      courseDetails: apiCourseDetails,
      classDetails: apiClassDetails,
      ...createPollResponse
    } = await createPollAPI({ classId, toBeDone });

    const courseDetails = courseFactory(apiCourseDetails);
    setCourse(courseDetails);

    const classDetails = classFactory(apiClassDetails);
    setClass(classDetails);

    await setSlideType({
      classId,
      activityId: createPollResponse._id,
      slideType,
    });

    createPollResponse.details.isAddedToPPT = 1;

    const createPollQuestionResponse = await createPollQuestion({ classId, pollId: createPollResponse._id });
    createPollResponse.details.questionSet = 1;

    const poll = pollFactory({
      currentUserId: session.userId,
      courseId,
      classId,
      pollId: createPollResponse._id,
      apiPoll: createPollResponse.details,
      publishPreferences: createPollResponse.publishDefaults,
      apiQuestion: createPollQuestionResponse,
      pollSubmissions: createPollResponse.stats?.numSubmitted ?? 0,
    });

    setPoll(poll);

    setSlideTitleAndSubtitle({
      title: t('acadly_activity_plugin_course_code', { ns: i18nNS.COMMON, courseCode: courseDetails.code }),
      subtitle: t('poll_was_not_conducted', { ns: i18nNS.POLL }),
    });

    const pollUrl = routes.poll.url({
      universitySlug,
      courseId,
      classId,
      pollId: poll.id,
      slideType,
    });

    const _slideType = enumFromStringValue(SlideType, slideTypeValues);
    if (!_slideType) return;

    setActivityType(presentationId, activeSlideId, _slideType, pollUrl);

    navigate(pollUrl, { replace: true });
  }, [presentationId, activeSlideId, session?.userId, universitySlug, courseId, classId, toBeDone]);

  useEffect(() => {
    createPoll();
  }, [createPoll]);
};

export default useNewPollPage;
